/**
 * DuplicateTicketsPage — F17 (Iter 136, Feb 2026).
 *
 * Lists Zoho Desk tickets that look like duplicates (same phone or email,
 * ≥ 2 open tickets within 48 h). Admin/Leader picks a primary, marks the
 * others as merged. Backed by `ticket_dedup_service.py`.
 */

import { useEffect, useState } from "react";
import { ticketDuplicatesApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, GitMerge, Phone, Mail, RefreshCw, ExternalLink } from "lucide-react";

function DuplicateGroup({ group, onMerged }) {
  const [primaryId, setPrimaryId] = useState(group.suggested_primary_id);
  const [busy, setBusy] = useState(false);

  const merge = async () => {
    if (!primaryId) {
      toast.error("Pick a primary ticket first");
      return;
    }
    const mergedIds = group.tickets.filter((t) => t.id !== primaryId).map((t) => t.id);
    setBusy(true);
    try {
      const r = await ticketDuplicatesApi.merge(group.key, primaryId, mergedIds);
      toast.success(`Merged ${r.data?.updated || mergedIds.length} ticket(s) into primary`);
      onMerged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[--border] rounded-2xl overflow-hidden" data-testid={`dup-group-${group.key}`}>
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {group.key_type === "phone"
            ? <Phone size={14} className="text-amber-700" />
            : <Mail size={14} className="text-amber-700" />}
          <span className="font-bold text-sm text-amber-800">
            {group.key_type === "phone" ? `+${group.key}` : group.key}
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
            {group.ticket_count} tickets
          </span>
        </div>
        <button
          onClick={merge}
          disabled={busy}
          className="px-3 py-1.5 rounded-full bg-amber-600 text-white text-xs font-bold inline-flex items-center gap-1.5 hover:bg-amber-700 disabled:opacity-60"
          data-testid={`dup-merge-${group.key}`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <GitMerge size={12} />}
          Merge into primary
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[--bg-cream]">
          <tr>
            <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Primary</th>
            <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Ticket #</th>
            <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Subject</th>
            <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Status</th>
            <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Created</th>
            <th className="text-right text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Msgs</th>
          </tr>
        </thead>
        <tbody>
          {group.tickets.map((t) => (
            <tr key={t.id} className="border-t border-[--border]">
              <td className="px-3 py-2">
                <input
                  type="radio"
                  name={`primary-${group.key}`}
                  checked={primaryId === t.id}
                  onChange={() => setPrimaryId(t.id)}
                  data-testid={`dup-primary-${t.id}`}
                />
              </td>
              <td className="px-3 py-2 font-mono text-[--text-primary] font-bold">
                #{t.zoho_ticket_number || t.zoho_ticket_id?.slice(0, 8)}
              </td>
              <td className="px-3 py-2 text-[--text-secondary] truncate max-w-[320px]">{t.subject || "—"}</td>
              <td className="px-3 py-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t.status || "Open"}</span>
              </td>
              <td className="px-3 py-2 text-[11px] text-[--text-muted]">{t.created_at ? new Date(t.created_at).toLocaleString("en-IN") : "—"}</td>
              <td className="px-3 py-2 text-right font-bold text-[--text-primary]">{t.message_count || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DuplicateTicketsPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(48);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const r = await ticketDuplicatesApi.list(windowHours, 50);
      setGroups(r.data?.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load duplicates");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    fetchGroups();
    // fetchGroups is windowHours-derived; we only want to refetch when
    // the time window changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowHours]);

  const role = user?.role;
  if (!["admin", "leader", "super_admin"].includes(role)) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-2xl">
          <h2 className="text-lg font-bold text-amber-800">Admins & leaders only</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="duplicate-tickets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>
            <GitMerge size={24} className="inline mr-2 -mt-1 text-amber-600" />
            Duplicate tickets
          </h1>
          <p className="text-sm text-[--text-muted] mt-1">
            Same customer opened multiple tickets in the last {windowHours} h. Merge them so the team replies once.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
            className="px-3 py-1.5 border border-[--border] rounded-full text-xs"
            data-testid="dup-window-select"
          >
            <option value={24}>Last 24 h</option>
            <option value={48}>Last 48 h</option>
            <option value={168}>Last 7 days</option>
          </select>
          <button onClick={fetchGroups} className="btn-icon" title="Refresh" data-testid="dup-refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={20} className="animate-spin inline text-[--text-muted]" /></div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-dashed border-[--border] rounded-2xl p-12 text-center" data-testid="dup-empty">
          <GitMerge size={32} className="mx-auto mb-3 text-[--text-muted] opacity-40" />
          <p className="text-sm text-[--text-secondary] font-bold">No duplicate tickets</p>
          <p className="text-xs text-[--text-muted] mt-1">
            Nice — every customer is in exactly one ticket within the last {windowHours} h.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <DuplicateGroup key={g.key} group={g} onMerged={fetchGroups} />
          ))}
        </div>
      )}
    </div>
  );
}
